import type { ParagraphMetricsChecklistImage as ParagraphMetricsChecklistImageData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphMetricsChecklistImage.module.css";

/**
 * Layout 63 - `PARAGRAPH_METRICS_CHECKLIST_IMAGE_LAYOUT`: 2-kol split.
 *
 * Struktura:
 *   - Lewa kolumna:
 *       - Eyebrow (text_1, uppercase accent)
 *       - Tytul H2 (name)
 *       - Desc (text, paragraph)
 *       - Grid stat cards (metrics[] value + label, auto-fit)
 *       - Checklist (features[] z zielonymi checkami)
 *   - Prawa kolumna: pojedyncze zdjecie (image, np. screenshot PageSpeed)
 */
export function ParagraphMetricsChecklistImage({
  paragraph,
}: {
  paragraph: ParagraphMetricsChecklistImageData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: desc, metrics, features, image } = paragraph;
  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasDesc = desc && desc.trim() !== "";
  const hasMetrics = Array.isArray(metrics) && metrics.length > 0;
  const hasFeatures = Array.isArray(features) && features.length > 0;

  return (
    <section className={styles.section}>
      <div className={styles.left}>
        {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasDesc ? <p className={styles.desc}>{desc}</p> : null}

        {hasMetrics ? (
          <div className={styles.metrics}>
            {metrics.map((m, idx) => (
              <div key={idx} className={styles.metricCard}>
                {m.value ? <div className={styles.metricValue}>{m.value}</div> : null}
                {m.label ? <div className={styles.metricLabel}>{m.label}</div> : null}
              </div>
            ))}
          </div>
        ) : null}

        {hasFeatures ? (
          <ul className={styles.features}>
            {features.map((f, idx) => (
              <li key={idx} className={styles.feature}>
                <span className={styles.checkIcon} aria-hidden="true" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {image ? (
        <figure className={styles.right}>
          <img
            src={image.url}
            alt={image.description ?? title ?? ""}
            width={image.width ?? undefined}
            height={image.height ?? undefined}
            className={styles.image}
            loading="lazy"
          />
          {image.description ? (
            <figcaption className={styles.imageCaption}>{image.description}</figcaption>
          ) : null}
        </figure>
      ) : null}
    </section>
  );
}
